(function () {
  'use strict';

  const Kg = window.KgPricing;
  if (!Kg) {
    console.warn('[kg-live-price] kg-pricing-core.js must load first');
    return;
  }

  const LOG_PREFIX = '[kg-live-price]';
  const GWEB_BASE = Kg.constants.GWEB_BASE;

  function overridePriceContainer(container, formattedPrice) {
    if (!container || !formattedPrice) return;
    let targeted = false;
    container.querySelectorAll('.price-item--regular').forEach((node) => {
      node.textContent = formattedPrice;
      targeted = true;
    });
    container.querySelectorAll('.price-item--sale.price-item--last').forEach((node) => {
      node.textContent = formattedPrice;
      targeted = true;
    });
    container.querySelectorAll('.product-option.price-original').forEach((node) => {
      node.textContent = formattedPrice;
      targeted = true;
    });
    container.querySelectorAll('.price.price--end').forEach((node) => {
      node.textContent = formattedPrice;
      targeted = true;
    });
    if (targeted) container.classList.remove('hidden');
  }

  function recomputeAndOverride(el, gold) {
    if (!gold) return false;
    const weight = Kg.parseWeight(el.dataset.kgWeight);
    if (weight == null) return false;
    const prc = Kg.parseNum(el.dataset.kgPrc);
    const prcus = Kg.parseNum(el.dataset.kgPrcus);
    const unitPrice = Kg.computePrice({
      pr18: gold.pr18,
      usdRate: gold.dollar,
      weight,
      prc,
      prcus,
    });
    if (unitPrice == null) return false;
    const roundedUnit = Kg.roundToNearest5(unitPrice);
    const qty = Kg.parseNum(el.dataset.kgQuantity);
    const displayPrice = qty > 0 ? roundedUnit * qty : roundedUnit;
    overridePriceContainer(el, Kg.formatPrice(displayPrice));
    return true;
  }

  function getPriceContainer(sectionId) {
    return document.getElementById(`price-${sectionId}`);
  }
  function getProductForm(sectionId) {
    const root = document.getElementById(`ProductInfo-${sectionId}`);
    return root ? root.querySelector('product-form') : null;
  }
  function getGwebWeightNode(el) {
    return el ? el.querySelector('[data-kg-gweb-weight]') : null;
  }
  function setAtcDisabled(disable, productForm, button) {
    if (productForm && typeof productForm.toggleSubmitButton === 'function') {
      productForm.toggleSubmitButton(disable, disable ? window.variantStrings && window.variantStrings.soldOut : undefined);
      return;
    }
    if (!button) return;
    if (disable) button.setAttribute('disabled', 'disabled');
    else button.removeAttribute('disabled');
  }

  class KgLivePrice extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.initialSku = (this.dataset.initialSku || '').trim();
      this.variantAvailable = this.dataset.initialAvailable === 'true';
      this.unsubscribeGold = null;
      this.itemData = null;
      this.fetchController = null;

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.onVariantChange(event);
        });
      }

      this.unsubscribeGold = Kg.onGoldChange((gold) => {
        this.recompute(gold);
      });

      this.fetchItemData(this.initialSku);

      setTimeout(() => {
        this.revealPriceContainer();
        this.releasePending({ showThemePrice: true });
      }, 3000);
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      if (this.fetchController) this.fetchController.abort();
      if (this.unsubscribeGold) this.unsubscribeGold();
    }

    getSelfPriceContainer() {
      return getPriceContainer(this.sectionId);
    }

    async fetchItemData(sku) {
      const trimmed = (sku || '').trim();
      if (!trimmed) {
        this.releasePending({ showThemePrice: true });
        return;
      }
      if (this.fetchController) this.fetchController.abort();
      const controller = new AbortController();
      this.fetchController = controller;
      this.setPending(true);

      try {
        const url = `${GWEB_BASE}/Sup/api/fn6/by-mco/${encodeURIComponent(trimmed)}/`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (this.fetchController !== controller) return;

        this.itemData = {
          weight: Kg.parseWeight(String(data.go_cr || '')),
          prc: Kg.parseNum(String(data.prc || '')),
          prcus: Kg.parseNum(String(data.prcus || '')),
        };

        if (data.go_cr) {
          this.applyGwebWeight(`${Number(data.go_cr).toFixed(3)} g`);
        }

        const gold = Kg.getGold();
        if (gold) {
          this.recompute(gold);
        } else {
          this.releasePending({ showThemePrice: true });
        }
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        if (this.fetchController !== controller) return;
        this.itemData = {
          weight: Kg.parseWeight(this.dataset.kgWeight || ''),
          prc: Kg.parseNum(this.dataset.kgPrc || ''),
          prcus: Kg.parseNum(this.dataset.kgPrcus || ''),
        };
        const gold = Kg.getGold();
        if (gold) {
          this.recompute(gold);
        } else {
          this.releasePending({ showThemePrice: true });
        }
      }
    }

    revealPriceContainer() {
      const container = this.getSelfPriceContainer();
      if (!container) return;
      const skeleton = container.querySelector('.kg-price-skeleton');
      if (skeleton) {
        const hiddenDiv = skeleton.querySelector('[style*="visibility:hidden"], [style*="visibility: hidden"]');
        if (hiddenDiv) hiddenDiv.style.visibility = 'visible';
        const shimmer = skeleton.querySelector('[style*="kg-shimmer"], div[style*="position:absolute"]');
        if (shimmer) shimmer.remove();
        while (skeleton.firstChild) container.appendChild(skeleton.firstChild);
        skeleton.remove();
      }
    }

    recompute(gold) {
      if (!gold || !this.itemData) return;
      const container = this.getSelfPriceContainer();
      if (!container) return;
      const unitPrice = Kg.computePrice({
        pr18: gold.pr18,
        usdRate: gold.dollar,
        weight: this.itemData.weight,
        prc: this.itemData.prc,
        prcus: this.itemData.prcus,
      });
      if (unitPrice == null) {
        this.revealPriceContainer();
        this.releasePending({ showThemePrice: true });
        return;
      }
      const roundedUnit = Kg.roundToNearest5(unitPrice);
      this.revealPriceContainer();
      overridePriceContainer(container, Kg.formatPrice(roundedUnit));
      this.setPending(false);
    }

    onVariantChange(event) {
      if (!event || !event.data) return;
      if (event.data.sectionId !== this.sectionId) return;
      const variant = event.data.variant;
      if (variant && typeof variant.available === 'boolean') {
        this.variantAvailable = variant.available;
      }
      const sku = (variant && variant.sku ? variant.sku : '').trim();
      if (!sku) {
        this.releasePending({ showThemePrice: true });
        return;
      }
      this.fetchItemData(sku);
    }

    setPending(pending) {
      const priceEl = this.getSelfPriceContainer();
      if (priceEl) priceEl.classList.toggle('kg-price-pending', !!pending);
    }

    releasePending({ showThemePrice = true, enableAtc = true } = {}) {
      this.revealPriceContainer();
      const priceEl = this.getSelfPriceContainer();
      if (priceEl) {
        priceEl.classList.remove('kg-price-pending');
        if (showThemePrice) priceEl.classList.remove('hidden');
      }
      if (enableAtc) setAtcDisabled(!this.variantAvailable, getProductForm(this.sectionId), document.getElementById(`ProductSubmitButton-${this.sectionId}`));
    }

    applyGwebWeight(weight) {
      const node = getGwebWeightNode(this);
      if (!node) return;
      node.textContent = `Weight: ${weight}`;
      node.style.display = 'block';
    }
  }

  if (!customElements.get('kg-live-price')) {
    customElements.define('kg-live-price', KgLivePrice);
  }

  function overrideAllLivePrices(gold) {
    if (!gold) return;
    const els = document.querySelectorAll('[data-kg-live-price]');
    let count = 0;
    for (const el of els) {
      if (el.tagName && el.tagName.toLowerCase() === 'kg-live-price') continue;
      if (recomputeAndOverride(el, gold)) count += 1;
    }
    if (count > 0) console.log(`${LOG_PREFIX} updated ${count} live prices`);
  }

  function init() {
    overrideAllLivePrices(Kg.getGold());
    Kg.onGoldChange(overrideAllLivePrices);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  let observerMutating = false;
  let observerDebounce = null;
  const observer = new MutationObserver(() => {
    if (observerMutating) return;
    const gold = Kg.getGold();
    if (!gold) return;
    clearTimeout(observerDebounce);
    observerDebounce = setTimeout(() => {
      observerMutating = true;
      try {
        overrideAllLivePrices(gold);
      } finally {
        observerMutating = false;
      }
    }, 300);
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
